import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener, ChangeDetectorRef, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from 'src/services/logger-services/logger.service';
// TrieNode and Trie classes for efficient searching
class TrieNode {
  children: { [key: string]: TrieNode } = {};
  isEndOfWord: boolean = false;
  domainCode: string = ''; // Store the domain code (e.g., 'MPR')
  matchedText: string = ''; // Store what text was matched (code or name part)
}

class Trie {
  root: TrieNode = new TrieNode();

  insert(domainCode: string, searchableTexts: string[]): void {
    // Insert each searchable text (domain code and full name words)
    searchableTexts.forEach(text => {
      let node = this.root;
      const normalizedText = text.toLowerCase();
      
      for (const char of normalizedText) {
        if (!node.children[char]) {
          node.children[char] = new TrieNode();
        }
        node = node.children[char];
      }
      node.isEndOfWord = true;
      node.domainCode = domainCode;
      node.matchedText = text;
    });
  }

  search(prefix: string): string[] {
    let node = this.root;
    const normalizedPrefix = prefix.toLowerCase();
    
    for (const char of normalizedPrefix) {
      if (!node.children[char]) {
        return [];
      }
      node = node.children[char];
    }
    
    const matchedCodes = new Set<string>();
    this.collectDomainCodes(node, matchedCodes);
    return Array.from(matchedCodes);
  }

  private collectDomainCodes(node: TrieNode, results: Set<string>): void {
    if (node.isEndOfWord && node.domainCode) {
      results.add(node.domainCode);
    }
    for (const char in node.children) {
      this.collectDomainCodes(node.children[char], results);
    }
  }
}

@Component({
  selector: 'app-domain-switch',
  templateUrl: './domain-switch.component.html',
  styleUrls: ['./domain-switch.component.css']
})
export class DomainSwitchComponent implements OnChanges {
  @Input() allDomainNames: string[] = [];
  @Input() isNewChatbotEnabled: boolean= false;
  @Input() textareaElement: HTMLElement | null | undefined = null;
  @Input() currentSelectedDomain: string = '';
  @Output() suggestionSelected = new EventEmitter<string>();
  @Output() domainInserted = new EventEmitter<void>();
  @Output() newChatRequested = new EventEmitter<void>();
  /** Emitted when the @-mention suggestions dropdown opens (true) or closes (false). Parent can use this to avoid sending on Enter while dropdown is open. */
  @Output() suggestionsVisible = new EventEmitter<boolean>();

  filteredSuggestions: string[] = [];
  showSuggestions = false;
  selectedSuggestionIndex: number = -1;
  trie = new Trie();
  showDomainPopup: boolean = false;
  selectedDomain: string = '';
  previousSelectedDomain: string = '';
  ifPromptAtBottom: boolean = false;
  /** Saved when suggestions are shown; used when inserting after click (focus has moved to dropdown). */
  private lastCaretPositionWhenSuggestionsShown: number = 0;
  private lastAtIndexWhenSuggestionsShown: number = -1;

  private readonly DomainSwitchError = (error: any)=>
    `[DomainSwitch] ${JSON.stringify(error)}`;

  constructor(
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly loggerService: LoggerService,
    private readonly elementRef: ElementRef
  ) {
    // Subscribe to language changes to rebuild trie with new translations
    this.translate.onLangChange.subscribe(() => {
      if (this.allDomainNames.length > 0) {
        // Small delay to ensure translations are loaded
        setTimeout(() => {
          if (this.isNewChatbotEnabled) {
            this.rebuildTrie();
          }
          // Force change detection to update domain names in the UI
          // This ensures the dropdown and suggestions show translated names
          this.cdr.detectChanges();
        }, 100);
      }
    });
  }
  
  ngOnInit() {
    try {
      if (this.isNewChatbotEnabled) {
        // Populate the trie with both domain codes and full domain names
        this.allDomainNames.forEach(domainCode => {
          const fullName = this.getDomainFullName(domainCode);
          const searchableTexts = this.getSearchableTexts(domainCode, fullName);
          this.trie.insert(domainCode, searchableTexts);
        });
      } else {
        if (this.allDomainNames.length > 0) {
          this.selectedDomain = this.allDomainNames[0];
          this.previousSelectedDomain = this.allDomainNames[0];
        }
      }
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "ngOnInit");
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    try {
      // This reliably attaches the input event listener to the contenteditable div
      if (this.isNewChatbotEnabled && changes['textareaElement'] && this.textareaElement) {
        const oldElement = changes['textareaElement'].previousValue as HTMLElement;
        if (oldElement) {
          oldElement.removeEventListener('input', this.updateSuggestions);
        }
        this.textareaElement.addEventListener('input', this.updateSuggestions);
      }
      
      // Handle changes to currentSelectedDomain
      if (changes['currentSelectedDomain'] && this.currentSelectedDomain) {
        this.selectedDomain = this.currentSelectedDomain;
        this.previousSelectedDomain = this.currentSelectedDomain;
      }
      
      // Rebuild trie when domain names change
      if (this.isNewChatbotEnabled && changes['allDomainNames'] && this.allDomainNames.length > 0) {
        this.rebuildTrie();
      }
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "ngOnChanges");
    }
  }

  /**
   * Rebuild the trie with updated domain names and translations
   */
  private rebuildTrie(): void {
    try {
      this.trie = new Trie();
      this.allDomainNames.forEach(domainCode => {
        const fullName = this.getDomainFullName(domainCode);
        const searchableTexts = this.getSearchableTexts(domainCode, fullName);
        this.trie.insert(domainCode, searchableTexts);
      });
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "rebuildTrie");
    }
  }

  /**
   * Main logic to show, hide, and position the suggestions box.
   * This is an arrow function to preserve the 'this' context.
   */
  updateSuggestions = (): void => {
    try {
      if (!this.textareaElement) {
        this.showSuggestions = false;
        this.suggestionsVisible.emit(false);
        return;
      }

      const currentValue = this.getInputValue();
      const caretPosition = this.getCaretPosition();
      
      // Enhanced @ detection - find the @ symbol before the caret
      const textBeforeCaret = currentValue.substring(0, caretPosition);
      const lastAtIndex = textBeforeCaret.lastIndexOf('@');
      
      if (lastAtIndex !== -1) {
        // Check if there's whitespace between @ and caret, if so, don't show suggestions
        const textAfterAt = textBeforeCaret.substring(lastAtIndex + 1);
        
        // Only show suggestions if there's no whitespace after @ and we're directly typing after it
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          const prefix = textAfterAt;
          
          // If prefix is empty (just @), show all domains
          if (prefix === '') {
            this.filteredSuggestions = this.allDomainNames;
          } else {
            this.filteredSuggestions = this.trie.search(prefix);
          }
          
          this.showSuggestions = this.filteredSuggestions.length > 0;
          this.suggestionsVisible.emit(this.showSuggestions);

          // Save caret/context so we can insert correctly when user clicks (focus will have moved to dropdown)
          if (this.showSuggestions) {
            this.lastCaretPositionWhenSuggestionsShown = caretPosition;
            this.lastAtIndexWhenSuggestionsShown = lastAtIndex;
            this.positionSuggestionsBox();
          }

          this.selectedSuggestionIndex = 0;
          return;
        }
      }

      // Hide suggestions if no valid @ mention context
      this.showSuggestions = false;
      this.suggestionsVisible.emit(false);
      this.filteredSuggestions = [];
      this.selectedSuggestionIndex = -1;
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "updateSuggestions");
    }
  }

  /**
   * Position the suggestions box near the @ symbol
   */
  private positionSuggestionsBox(): void {
    try {
      if (typeof document === 'undefined') {
        return;
      }
      
      setTimeout(() => {
        try {
          // Update ifPromptAtBottom before positioning
          this.updateSuggestionsPosition();
          
          const coords = this.getCaretPixelPosition();
          // Use this instance's container (host parent) so each instance positions its own dropdown
          const domainSwitchContainer = (this.elementRef?.nativeElement?.parentElement ||
            document.querySelector('.domain-switch-container')) as HTMLElement;

          if (domainSwitchContainer && coords) {
            try {
              const containerHeight = domainSwitchContainer.offsetHeight || 0;
              const verticalGap = 5;
              const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 600;
              
              // Calculate if suggestions should appear above or below
              const spaceBelow = viewportHeight - coords.top;
              const shouldShowAbove = spaceBelow < containerHeight + 50;
              
              let newTop: number;
              if (shouldShowAbove) {
                newTop = coords.top - containerHeight - verticalGap;
                // If prompt is at bottom, reduce the top position by 100px
                if (this.ifPromptAtBottom) {
                  newTop -= 256;
                }
                domainSwitchContainer.style.top = `${Math.max(5, newTop)}px`;
              } else {
                newTop = coords.top + 25;
                // If prompt is at bottom, reduce the top position by 100px
                if (this.ifPromptAtBottom) {
                  newTop -= 256;
                }
                domainSwitchContainer.style.top = `${newTop}px`;
              }
              
              domainSwitchContainer.style.left = `${coords.left}px`;
              domainSwitchContainer.style.position = 'fixed';
              domainSwitchContainer.style.zIndex = '1000';
            } catch (styleError) {
              this.loggerService.logError(this.DomainSwitchError(styleError), "positionSuggestionsBox-styling");
            }
          } else if (!coords) {
            this.loggerService.logError(this.DomainSwitchError('Unable to get caret position'), "positionSuggestionsBox");
          } else if (!domainSwitchContainer) {
            this.loggerService.logError(this.DomainSwitchError('Domain switch container not found'), "positionSuggestionsBox");
          }
        } catch (positionError) {
          this.loggerService.logError(this.DomainSwitchError(positionError), "positionSuggestionsBox-position");
        }
      }, 0);
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "positionSuggestionsBox");
    }
  }

  /**
   * Update the ifPromptAtBottom status by checking prompt section CSS classes
   */
  private updateSuggestionsPosition(): void {
    try {
      if (typeof document === 'undefined') {
        return;
      }
      
      const promptSection = document.querySelector('.prompt-section') as HTMLElement;
      if (promptSection) {
        try {
          // Check if prompt section has classes that indicate it's at the bottom
          const hasNewSession = promptSection.classList.contains('new-session');
          const hasCollapsed = promptSection.classList.contains('collapsed');
          const hasNarrow = promptSection.classList.contains('prompt-section--narrow');
          
          // Prompt is at bottom if:
          // 1. It has 'new-session' class, OR
          // 2. It has both 'collapsed' and 'prompt-section--narrow' classes
          this.ifPromptAtBottom = hasNewSession || (hasCollapsed && hasNarrow);
        } catch (classError) {
          this.loggerService.logError(this.DomainSwitchError(classError), "updateSuggestionsPosition-classList");
          // Default to safe assumption
          this.ifPromptAtBottom = false;
        }
      } else {
        // If prompt section not found, assume it's not at bottom
        this.ifPromptAtBottom = false;
      }
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "updateSuggestionsPosition");
      this.ifPromptAtBottom = false;
    }
  }

  /**
   * Generically gets the text value from either a <textarea> or a contenteditable <div>.
   */
  private getInputValue(): string {
    if (!this.textareaElement) return '';
    try {
      if (this.textareaElement instanceof HTMLTextAreaElement || this.textareaElement instanceof HTMLInputElement) {
        return this.textareaElement.value || '';
      }
      return (this.textareaElement as HTMLElement).innerText || (this.textareaElement as HTMLElement).textContent || '';
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "getInputValue");
      return '';
    }
  }

  /**
   * Gets the current caret position in the text
   */
  private getCaretPosition(): number {
    if (!this.textareaElement) return 0;
    if (this.textareaElement instanceof HTMLTextAreaElement || this.textareaElement instanceof HTMLInputElement) {
      return this.textareaElement.selectionStart || 0;
    }
    
    // For contenteditable elements
    if (typeof window === 'undefined') return 0;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return 0;
    }

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(this.textareaElement);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    
    return preCaretRange.toString().length;
  }

  /**
   * Calculates the caret's pixel position within the viewport for a contenteditable element.
   */
  private getCaretPixelPosition(): { top: number; left: number } | null {
    if (typeof window === 'undefined') return null;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }
    
    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);

    const tempSpan = document.createElement('span');
    range.insertNode(tempSpan);

    const rect = tempSpan.getBoundingClientRect();
    const position = { top: rect.top, left: rect.left };
    
    tempSpan.remove();
    return position;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    try {
      const target = event.target as HTMLElement;
      // Handle closing @suggestions if new chatbot is enabled
      if (this.isNewChatbotEnabled && this.textareaElement) {
        const clickedInsideSuggestions = this.textareaElement.contains(target) || target.closest('.suggestions-box');
        if (!clickedInsideSuggestions) {
          this.showSuggestions = false;
          this.suggestionsVisible.emit(false);
        }
      }

      // Handle closing the domain popup
      const clickedInsidePopup = target.closest('.domain-popup-content') || target.closest('button'); // Assuming the button to open is also considered 'inside'
      if (this.showDomainPopup && !clickedInsidePopup) {
          this.showDomainPopup = false;
      }
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "handleClickOutside");
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    try {
      if (this.isNewChatbotEnabled && this.showSuggestions && this.filteredSuggestions.length > 0) {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          // Ensure selectedSuggestionIndex is valid
          if (this.selectedSuggestionIndex >= 0 && this.selectedSuggestionIndex < this.filteredSuggestions.length) {
            this.selectSuggestion(this.filteredSuggestions[this.selectedSuggestionIndex]);
          } else if (this.filteredSuggestions.length > 0) {
            // Default to first suggestion if index is invalid
            this.selectSuggestion(this.filteredSuggestions[0]);
          }
          return;
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          event.stopPropagation();
          this.selectedSuggestionIndex = (this.selectedSuggestionIndex + 1) % this.filteredSuggestions.length;
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          event.stopPropagation();
          this.selectedSuggestionIndex = (this.selectedSuggestionIndex - 1 + this.filteredSuggestions.length) % this.filteredSuggestions.length;
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.showSuggestions = false;
          this.suggestionsVisible.emit(false);
          this.filteredSuggestions = [];
          this.selectedSuggestionIndex = -1;
        }
      } else if (this.isNewChatbotEnabled && event.key === 'Backspace') {
        setTimeout(this.updateSuggestions, 0);
      }
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "handleKeyboardEvent");
    }
  }

  selectSuggestion(domain: string) {
    try {
      if (!domain) {
        this.loggerService.logError(this.DomainSwitchError('Invalid domain provided'), "selectSuggestion");
        return;
      }

      if (this.isNewChatbotEnabled && this.textareaElement) {
        this.insertDomainMention(domain);
        
        // Emit event for highlight ing after a delay to ensure cursor is set first
        setTimeout(() => {
          this.domainInserted.emit();
        }, 100);
      } else {
        // Check if the selected domain is different from the previous one
        const isDifferentDomain = domain !== this.previousSelectedDomain;
        
        this.selectedDomain = domain;
        
        // Only emit new chat request if a different domain is selected
        if (isDifferentDomain) {
          this.newChatRequested.emit();
          this.previousSelectedDomain = domain;
        } 
      }
      
      this.suggestionSelected.emit(domain);
      
      // Focus back to the textarea after selection with a longer delay
      if (this.textareaElement) {
        setTimeout(() => {
          try {
            if (!this.textareaElement) return;
            this.textareaElement.focus();
            
            // Ensure cursor is at the end after focus
            setTimeout(() => {
              try {
                if (!this.textareaElement) return;
                const textContent = this.textareaElement.textContent || '';
                this.setCaretPosition(textContent.length);
              } catch (focusError) {
                this.loggerService.logError(this.DomainSwitchError(focusError), "selectSuggestion-caretPosition");
              }
            }, 50);
          } catch (focusError) {
            this.loggerService.logError(this.DomainSwitchError(focusError), "selectSuggestion-focus");
          }
        }, 150);
      }
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "selectSuggestion");
    } finally {
      // Always ensure UI state is reset regardless of errors
      this.showSuggestions = false;
      this.suggestionsVisible.emit(false);
      this.showDomainPopup = false;
      this.selectedSuggestionIndex = -1;
    }
  }

  /**
   * True if the current selection/focus is inside our textarea (contenteditable).
   * When user clicks a suggestion, focus moves to the dropdown so we must use saved position.
   */
  private isSelectionInTextarea(): boolean {
    if (typeof window === 'undefined' || !this.textareaElement) return false;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const node = sel.anchorNode;
    return node ? this.textareaElement.contains(node) : false;
  }

  /**
   * Insert the selected domain mention into the contenteditable element
   */
  private insertDomainMention(domain: string): void {
    if (!this.textareaElement) return;
    if (!domain || !this.textareaElement) {
      return;
    }
    
    try {
      const currentValue = this.getInputValue();
      if (currentValue === null || currentValue === undefined) {
        return;
      }
      // When user clicked a suggestion, focus moved to dropdown so getCaretPosition() would be wrong; use saved values
      const useSaved = !this.isSelectionInTextarea();
      const caretPosition = useSaved ? this.lastCaretPositionWhenSuggestionsShown : this.getCaretPosition();
      const textBeforeCaret = currentValue.substring(0, caretPosition);
      const lastAtIndex = useSaved && this.lastAtIndexWhenSuggestionsShown >= 0
        ? this.lastAtIndexWhenSuggestionsShown
        : textBeforeCaret.lastIndexOf('@');
      
      if (lastAtIndex !== -1) {
        try {
          // Insert full name visible in textarea, with code in parentheses (e.g. "@Medicinal Products (MPR) ")
          const fullName = this.getDomainFullName(domain);
          const mentionText = `@${fullName} `;
          const beforeAt = currentValue.substring(0, lastAtIndex);
          const afterCaret = currentValue.substring(caretPosition);
          const newValue = beforeAt + mentionText + afterCaret;
          const newCaretPos = lastAtIndex + mentionText.length;

          // Update the content
          if (this.textareaElement instanceof HTMLTextAreaElement || this.textareaElement instanceof HTMLInputElement) {
            this.textareaElement.value = newValue;
            try {
              this.textareaElement.setSelectionRange(newCaretPos, newCaretPos);
            } catch (selectionError) {
              this.loggerService.logError(this.DomainSwitchError(selectionError), "insertDomainMention-setSelection");
            }
          } else {
            // For contenteditable elements
            this.updateContentEditableValue(newValue, newCaretPos);
          }

          // Trigger input event to update any bound models
          this.triggerInputEvent();
        } catch (updateError) {
          this.loggerService.logError(this.DomainSwitchError(updateError), "insertDomainMention-update");
          // Fallback: try to simply append the domain mention with full name
          try {
            const fullName = this.getDomainFullName(domain);
            const mentionText = `@${fullName} `;
            if (this.textareaElement instanceof HTMLTextAreaElement || this.textareaElement instanceof HTMLInputElement) {
              this.textareaElement.value = currentValue + mentionText;
            } else {
              this.textareaElement.textContent = currentValue + mentionText;
            }
            this.triggerInputEvent();
          } catch (fallbackError) {
            this.loggerService.logError(this.DomainSwitchError(fallbackError), "insertDomainMention-fallback");
          }
        }
      }
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "insertDomainMention");
    }
  }

  /**
   * Update contenteditable element value and set caret position
   */
  private updateContentEditableValue(newValue: string, caretPosition: number): void {
    if (!this.textareaElement) return;
    this.textareaElement.innerText = newValue;
    
    // Set caret position after a short delay to ensure DOM is updated
    setTimeout(() => {
      this.setCaretPosition(caretPosition);
    }, 0);
  }

  /**
   * Set caret position in contenteditable element
   */
  private setCaretPosition(position: number): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      if (!this.textareaElement) {
        return;
      }
      
      const selection = window.getSelection();
      if (!selection) {
        return;
      }

      const element = this.textareaElement;
      const textContent = element.textContent || '';
      const safePosition = Math.min(Math.max(0, position), textContent.length);
      
      try {
        // Create a range
        const range = document.createRange();
        
        // Find the text node and position
        let currentPos = 0;
        let targetNode = null;
        let targetOffset = 0;
        
        try {
          // Walk through all text nodes
          const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null
          );
          
          let node;
          while (node = walker.nextNode()) {
            const nodeLength = node.textContent?.length || 0;
            if (currentPos + nodeLength >= safePosition) {
              targetNode = node;
              targetOffset = safePosition - currentPos;
              break;
            }
            currentPos += nodeLength;
          }
        } catch (walkerError) {
          this.loggerService.logError(this.DomainSwitchError(walkerError), "setCaretPosition-walker");
        }
        
        // If no text node found, create one at the end
        if (!targetNode && element.childNodes.length === 0) {
          try {
            const textNode = document.createTextNode('');
            element.appendChild(textNode);
            targetNode = textNode;
            targetOffset = 0;
          } catch (nodeError) {
            this.loggerService.logError(this.DomainSwitchError(nodeError), "setCaretPosition-createNode");
            return;
          }
        }
        
        if (targetNode) {
          try {
            range.setStart(targetNode, targetOffset);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (rangeError) {
            this.loggerService.logError(this.DomainSwitchError(rangeError), "setCaretPosition-range");
            // Fallback: try to set cursor at the end of the element
            try {
              const fallbackRange = document.createRange();
              fallbackRange.selectNodeContents(element);
              fallbackRange.collapse(false);
              selection.removeAllRanges();
              selection.addRange(fallbackRange);
            } catch (fallbackError) {
              this.loggerService.logError(this.DomainSwitchError(fallbackError), "setCaretPosition-fallback");
            }
          }
        }
      } catch (rangeCreationError) {
        this.loggerService.logError(this.DomainSwitchError(rangeCreationError), "setCaretPosition-rangeCreation");
      }
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "setCaretPosition");
    }
  }

  /**
   * Trigger input event for model updates
   */
  private triggerInputEvent(): void {
    const event = new Event('input', { bubbles: true });
    if (!this.textareaElement) return;
    this.textareaElement.dispatchEvent(event);
  }

  openDomainPopup() {
    try {
      this.showDomainPopup = !this.showDomainPopup;
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "openDomainPopup");
    }
  }
  /**
   * Get full domain name based on domain code using translations
   */
  getDomainFullName(domainCode: string): string {
    try {
      if (!domainCode || typeof domainCode !== 'string') {
        return domainCode || '';
      }

      const translationKey = `DOMAIN_${domainCode.toUpperCase()}`;
      
      try {
        const translation = this.translate.instant(translationKey);
        
        // If translation exists and is not the same as the key, use it
        if (translation && translation !== translationKey) {
          return translation;
        }
      } catch (translationError) {
        this.loggerService.logError(this.DomainSwitchError(translationError), "getDomainFullName-translation");
      }
      
      // Fallback to English names if translation is not available
      const fallbackNames: { [key: string]: string } = {
        'MPR': 'Medicinal Products',
        'COS': 'Cosmetics',
        'MDV': 'Medical Devices', 
        'CHP': 'Chemicals and Household Products',
        'FDS': 'Food and Food Supplements',
      };
      
      const upperCaseCode = domainCode.toUpperCase();
      return fallbackNames[upperCaseCode] || domainCode;
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "getDomainFullName");
      return domainCode || 'Unknown Domain';
    }
  }

  /**
   * Get domain icon based on domain code
   */
  getdomainIcon(domainCode: string): string {
    try {
      const icons: { [key: string]: string } = {
        'MPR': '../../assets/icons/icon-medicinal-products.svg',
        'COS': '../../assets/icons/icon-cosmetics.svg',
        'MDV': '../../assets/icons/icon-medical-devices.svg',
        'CHP': '../../assets/icons/icon-chemicals-and-household-products.svg',
        'FDS': '../../assets/icons/icon-food-and-food-supplements.svg',
      };
      return icons[domainCode.toUpperCase()] || '';
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "getdomainIcon");
      return '';
    }
  }

  /**
   * Get searchable texts for a domain including code, full name, and translations
   */
  private getSearchableTexts(domainCode: string, fullName: string): string[] {
    try {
      const searchableTexts: string[] = [];
      
      // Add the domain code itself
      searchableTexts.push(domainCode);
      
      // Add the full English name and its individual words
      searchableTexts.push(fullName);
      const englishWords = fullName.toLowerCase().split(/\s+/);
      searchableTexts.push(...englishWords);
      
      // Add translated names from i18n for current language
      const translationKey = `DOMAIN_${domainCode.toUpperCase()}`;
      
      try {
        const translation = this.translate.instant(translationKey);
        if (translation && translation !== translationKey && translation !== fullName) {
          searchableTexts.push(translation);
          // Also add individual words from translations
          const translatedWords = translation.toLowerCase().split(/\s+/);
          searchableTexts.push(...translatedWords);
        }
      } catch (translationError) {
        // Silently handle translation errors
        this.loggerService.logError(this.DomainSwitchError(translationError), "getSearchableTexts-translation");
      }
      
      // Remove duplicates and empty strings
      return [...new Set(searchableTexts.filter(text => text && text.trim().length > 0))];
    } catch (error) {
      this.loggerService.logError(this.DomainSwitchError(error), "getSearchableTexts");
      return [domainCode];
    }
  }

}